'use client';

import type { PDCardCalibrationEventCode } from '@zeelool/pd-card-calibration';

interface MeasurementAudioClock {
  now: () => number;
  setTimeout: (callback: () => void, delayMs: number) => number;
  clearTimeout: (timer: number) => void;
}

interface MeasurementAudioPlayer {
  play: (code: PDCardCalibrationEventCode) => Promise<void>;
  stop: () => void;
}

const DEFAULT_CLOCK: MeasurementAudioClock = {
  now: () => performance.now(),
  setTimeout: (callback, delayMs) => window.setTimeout(callback, delayMs),
  clearTimeout: (timer) => window.clearTimeout(timer),
};

export interface MeasurementAudioScheduler {
  updatePrompt: (code: PDCardCalibrationEventCode) => void;
  setMuted: (muted: boolean) => void;
  reset: () => void;
  dispose: () => void;
}

export function createMeasurementAudioScheduler({
  player,
  playableCodes,
  stepEnterDelayMs,
  interPromptGapMs,
  onError,
  clock = DEFAULT_CLOCK,
}: {
  player: MeasurementAudioPlayer;
  playableCodes: ReadonlySet<PDCardCalibrationEventCode>;
  stepEnterDelayMs: number;
  interPromptGapMs: number;
  onError: (error: unknown, code: PDCardCalibrationEventCode) => void;
  clock?: MeasurementAudioClock;
}): MeasurementAudioScheduler {
  let isMuted = true;
  let isDisposed = false;
  let isPlaying = false;
  let currentCode: PDCardCalibrationEventCode | null = null;
  let currentEventRevision = 0;
  let audioPlaybackRevision = 0;
  let measurementRoundId = 0;
  let currentEventEnteredAt = 0;
  let lastAudioEndedAt = Number.NEGATIVE_INFINITY;
  let lastFailedEventRevision: number | null = null;
  let timer: number | null = null;

  const clearTimer = () => {
    if (timer !== null) {
      clock.clearTimeout(timer);
      timer = null;
    }
  };

  const stopActivePlayback = () => {
    audioPlaybackRevision += 1;
    isPlaying = false;
    try {
      player.stop();
    } catch (error) {
      if (currentCode) onError(error, currentCode);
    }
  };

  const scheduleCurrent = () => {
    clearTimer();
    if (isDisposed || isMuted || isPlaying || !currentCode || !playableCodes.has(currentCode)) {
      return;
    }

    const code = currentCode;
    const eventRevision = currentEventRevision;
    const roundId = measurementRoundId;
    const eligibleAt = Math.max(
      currentEventEnteredAt + Math.max(0, stepEnterDelayMs),
      lastAudioEndedAt + Math.max(0, interPromptGapMs),
    );
    const delayMs = Math.max(0, eligibleAt - clock.now());

    timer = clock.setTimeout(() => {
      timer = null;
      if (
        isDisposed ||
        isMuted ||
        isPlaying ||
        roundId !== measurementRoundId ||
        eventRevision !== currentEventRevision ||
        code !== currentCode ||
        !playableCodes.has(code)
      ) {
        return;
      }

      isPlaying = true;
      const playbackRevision = ++audioPlaybackRevision;
      const handlePlaybackFailure = (error: unknown) => {
        isPlaying = false;
        lastAudioEndedAt = clock.now();
        onError(error, code);

        const hasNewerPrompt = currentEventRevision !== eventRevision;
        const shouldRetryCurrentPrompt = lastFailedEventRevision !== eventRevision;
        lastFailedEventRevision = eventRevision;
        if (hasNewerPrompt || shouldRetryCurrentPrompt) scheduleCurrent();
      };
      let playback: Promise<void>;
      try {
        playback = player.play(code);
      } catch (error) {
        handlePlaybackFailure(error);
        return;
      }

      void playback.then(
        () => {
          if (
            isDisposed ||
            isMuted ||
            playbackRevision !== audioPlaybackRevision ||
            roundId !== measurementRoundId
          ) {
            return;
          }
          isPlaying = false;
          lastFailedEventRevision = null;
          lastAudioEndedAt = clock.now();
          scheduleCurrent();
        },
        (error: unknown) => {
          if (
            isDisposed ||
            playbackRevision !== audioPlaybackRevision ||
            roundId !== measurementRoundId
          ) {
            return;
          }
          handlePlaybackFailure(error);
        },
      );
    }, delayMs);
  };

  return {
    updatePrompt: (code) => {
      if (isDisposed || code === currentCode) return;
      currentCode = code;
      currentEventRevision += 1;
      currentEventEnteredAt = clock.now();
      if (!isPlaying) scheduleCurrent();
    },
    setMuted: (muted) => {
      if (isDisposed || muted === isMuted) return;
      isMuted = muted;
      clearTimer();
      if (muted) {
        stopActivePlayback();
        return;
      }

      // Turning guidance back on starts a fresh entry-delay cycle for the latest prompt.
      currentEventRevision += 1;
      currentEventEnteredAt = clock.now();
      scheduleCurrent();
    },
    reset: () => {
      if (isDisposed) return;
      clearTimer();
      stopActivePlayback();
      measurementRoundId += 1;
      currentEventRevision += 1;
      currentCode = null;
      currentEventEnteredAt = 0;
      lastAudioEndedAt = Number.NEGATIVE_INFINITY;
      lastFailedEventRevision = null;
    },
    dispose: () => {
      if (isDisposed) return;
      clearTimer();
      stopActivePlayback();
      isDisposed = true;
      currentCode = null;
    },
  };
}
